import {React, ui, moment} from 'lib';

import {useRemoteData, Req, Res, useWriterApi, useForm, FormItem, Textarea, useSingleQueryValueAsNumber, useSingleQueryValueAsTrue, BoolCheckboxInput, ApiErrorDialog, ApiSpinnerDialog, ApiCompletionDialog, LayoutItem, useSelected} from 'shared';

import {Action} from 'types/elcstockbook/api/data.gen';
import {StaffFrame, useStore, StaffSelect} from './base';
import {ActionDetailRow} from './action/preview';
import {ActionDetailDialog} from './action/detail';
import {useModelDetailDialog, Model} from './model';
import {useQueryValue, ManagedFilter} from './model_filter';

const masterPath = 'staff/get_master_data';

const getKeepsPath = 'staff/get_keeps';
const freedPath = 'staff/freed';
type Keep = ArrayItem<Res<typeof getKeepsPath>>;


export function KeepPage(): React.ReactElement {
    const actionId = useSingleQueryValueAsNumber('action');
    const detailDisclosure = ui.useDisclosure({
        isOpen: actionId.value !== null,
        onClose: () => actionId.replace(null),
    });

    const keepsRef = React.useRef<Ref<typeof Keeps>>(null);

    const modelDetailDialog = useModelDetailDialog({
        onUpdated: (model) => {
            keepsRef.current?.updateModel(model);
        },
    });

    return <StaffFrame
        title="予約"
        hideFloatingContent={modelDetailDialog.isOpen}
    >
        <Keeps ref={keepsRef} />
        <ActionDetailDialog
            id={actionId.value}
            onUpdated={(action) => {
                keepsRef.current?.updateAction(action);
            }}
            {...detailDisclosure}
        />
        {modelDetailDialog.dialog}
    </StaffFrame>;
};


const Keeps = React.forwardRef<{
    updateAction(action: Action): void;
    updateModel(model: Model): void;
}, {}>((_props, ref) => {
    const store = useStore();
    const actionId = useSingleQueryValueAsNumber('action');
    const staffId = useSingleQueryValueAsNumber('staff');
    const desc = useSingleQueryValueAsTrue('desc');
    const hideWaitForCancel = useSingleQueryValueAsTrue('hide');

    const {data, reload, setData} = useRemoteData({
        path: getKeepsPath,
        request: {
            store_id: store.id,
            staff_id: staffId.value,
            ascending: !desc.value,
            ...useQueryValue(),
        }
    });

    const {data: master} = useRemoteData({
        path: masterPath,
        request: {
            store_id: store.id,
        },
    });

    React.useImperativeHandle(ref, () => ({
        updateAction: (action: Action) => {
            setData((old) => old.map(k => ({
                ...k,
                action: k.action.id === action.id ? action : k.action,
            })));
        },
        updateModel: (model: Model) => {
            setData((old) => old.map(k => {
                if (k.detail.item.product.model.id === model.id) {
                    const clone = {...k};
                    clone.detail.item.product.model = model;
                    return clone;
                }
                return k;
            }));
        },
    }));

    const [editing, setEditing] = React.useState<Keep>();
    const disclosure = ui.useDisclosure({
        isOpen: editing !== undefined,
        onClose: () => setEditing(undefined),
    });

    return <ui.Box my={10}>
        <LayoutItem>
            <ui.Heading>
                予約
            </ui.Heading>
        </LayoutItem>

        {master && <LayoutItem>
            <ui.Stack spacing={5}>
                <ManagedFilter
                    master={master}
                    showGenderFilter={false}
                    showSeasonFilter={false}
                    showCategoryFilter={false}
                    showLineFilter={false}
                    showColorFilter={false}
                    showSizeFilter={false}
                    showSaleFilter={false}
                />

                <StaffSelect
                    value={staffId.value}
                    onChange={(_, v) => staffId.push(v)}
                />

                <BoolCheckboxInput
                    value={desc.value ?? false}
                    onChange={(_, v) => desc.push(v || null)}
                    label="新しい予約を先頭に表示"
                />

                <BoolCheckboxInput
                    value={hideWaitForCancel.value ?? false}
                    onChange={(_, v) => hideWaitForCancel.push(v || null)}
                    label="キャンセル待ちを隠す"
                />
            </ui.Stack>
        </LayoutItem>}

        {data?.length === 0 && <LayoutItem>
            <ui.Text color="gray.500">
                条件に一致する予約はありません
            </ui.Text>
        </LayoutItem>}

        {data?.map((keep) => {
            const {action} = keep;
            if (hideWaitForCancel.value && keep.wait_for_cancel > 0) {
                return null;
            }
            return <LayoutItem key={keep.id}>
                <ui.Box border="1px solid #eee" borderRadius="4px" p={4} opacity={keep.wait_for_cancel > 0 ? 0.7 : 1}>
                    {keep.wait_for_cancel > 0 && <ui.Box bgColor="red.600" p="8px" mb={2}>
                        <ui.Text color="white">
                            キャンセル待ち{keep.detail.amount > 1 && `（${keep.wait_for_cancel}点）`}
                        </ui.Text>
                    </ui.Box>}

                    <ui.Flex justifyContent="space-between">
                        <ui.Box>
                            <ui.Text fontSize="sm" textDecoration="underline" cursor="pointer" onClick={() => actionId.push(action.id)}>
                                {moment(action.created_at).format('YYYY/MM/DD')}
                            </ui.Text>

                            <ui.Flex alignItems="center" mt={2}>
                                <ui.Text fontWeight="bold">
                                    {action.customer_name}様
                                </ui.Text>

                                <ui.Text ml={2} fontSize="small">
                                    by {action.staff.name}
                                </ui.Text>
                            </ui.Flex>
                        </ui.Box>

                        <ui.Button
                            onClick={() => setEditing(keep)}
                            colorScheme="blue"
                        >
                            予約解除
                        </ui.Button>
                    </ui.Flex>

                    {action.note && <ui.Text mt={2} whiteSpace="pre-line">
                        {action.note}
                    </ui.Text>}

                    <ItemPreview item={keep} />

                </ui.Box>
            </LayoutItem>;
        })}

        <FreeActionDialog
            keep={editing}
            onComplete={() => {
                disclosure.onClose();
                reload();
            }}
            {...disclosure}
        />
    </ui.Box>;
});


const ItemPreview = ({item}: {item: Keep}) => {
    return <ui.Table>
        <ui.Tbody>
            <ActionDetailRow detail={item.detail} canOpenDetail={true} />
        </ui.Tbody>
    </ui.Table>
};


const FreeActionDialog = ({keep, onComplete, ...props}: {
    keep: Keep | undefined;
    onComplete(): void;
} & ui.UseDisclosureReturn) => {
    const store = useStore();
    const api = useWriterApi(freedPath);
    const {binder, handleSubmit, reset} = useForm<Req<typeof freedPath>, 'keep_id'>(() => ({
        store_id: store.id,
        keep_id: keep?.id,
        note: '',
    }));

    React.useEffect(() => {
        if (props.isOpen) {
            reset();
        }
    }, [props.isOpen, keep, reset]);

    const setSelected = useSelected(keep?.detail.item.barcode ?? '')[1];

    const submit = handleSubmit(data => api.call(data));

    return <ui.Modal {...props}>
        <ui.ModalOverlay />
        <ui.ModalContent>
            <ui.ModalHeader textAlign="center">予約解除</ui.ModalHeader>
            <ui.ModalCloseButton />

            <ui.ModalBody>
                <FormItem label="Item">
                    {keep && <ItemPreview item={keep} />}
                </FormItem>

                <FormItem
                    label="note"
                    keyPath="note"
                    error={api.error?.data}
                >
                    <Textarea
                        {...binder.mapInputProps('note')}
                    />
                </FormItem>
            </ui.ModalBody>

            <ui.ModalFooter>
                <ui.Button onClick={props.onClose} mr={3}>キャンセル</ui.Button>
                <ui.Button
                    colorScheme="blue"
                    onClick={submit}
                >
                    確定
                </ui.Button>
            </ui.ModalFooter>
        </ui.ModalContent>

        <ApiErrorDialog api={api} onOk={api.reset} />
        <ApiSpinnerDialog api={api} />
        <ApiCompletionDialog
            api={api}
            onOk={() => {
                setSelected((cur) => cur + (keep?.detail.amount ?? 0));

                onComplete();
                reset();
                api.reset();
            }}
        />
    </ui.Modal>;
};
